import { useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PAGES } from '../../constants/pages';

import {
    useGetSupplyOrdersQuery,
    useSupplyOrdersUpdatedOrCreatedSubscription,
    type GetSupplyOrdersQueryVariables,
} from 'data/__generated__';

import { DataView } from 'components/DataView';
import {
    PageActions,
    PageActionsMenu,
    PageBreadcrumb,
    PageBreadcrumbSegment,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';

import { TrelloCardInfoPopup } from './Popups/TrelloCardInfoPopup';
import { supplyOrdersUpdatedOrCreatedHandler } from './cacheHandlers/supplyOrdersCacheHandler';
import { buildSupplyOrderActions } from './constants/supplyOrderActions';
import { getDefaultDisplayLimitDate, SUPPLY_ORDER_FILTERS } from './constants/supplyOrderFilters';
import { SUPPLY_ORDER_VIEWS } from './constants/supplyOrderViews';

// La date limite est le seul filtre qui change l'ensemble chargé. Le premier rendu part sur la même
// valeur que le filtre : la `DataView` ne remonte son état qu'après le montage, un périmètre différent
// ici ferait une requête pour rien — et un périmètre VIDE ferait lire toute la collection.
const INITIAL_VARIABLES: GetSupplyOrdersQueryVariables = {
    displayLimitDateFormatted: getDefaultDisplayLimitDate(),
};

export const SupplyOrders = () => {
    const navigate = useNavigate();
    const [isCardInfoPopupOpen, setIsCardInfoPopupOpen] = useState(false);

    const [variables, setVariables] = useState<GetSupplyOrdersQueryVariables>(INITIAL_VARIABLES);

    const handleFiltersChange = useCallback((filters: Record<string, unknown>) => {
        // Chip vide ⇒ `null`, et surtout pas un repli sur une date par défaut : effacer la borne doit
        // faire remonter les rangés/annulés plus anciens, sans quoi le chip n'a aucun effet visible.
        const displayLimitDateFormatted = (filters.displayLimitDate as string) || null;
        // Comparaison par valeur : la `DataView` remonte son état à chaque rendu, une identité neuve
        // relancerait la query en boucle.
        setVariables((current) =>
            current.displayLimitDateFormatted === displayLimitDateFormatted ? current : { displayLimitDateFormatted },
        );
    }, []);

    const {
        data: supplyOrdersData,
        loading: supplyOrdersLoading,
        error: supplyOrdersError,
    } = useGetSupplyOrdersQuery({ fetchPolicy: 'cache-and-network', variables });

    // La souscription vivait dans CHAQUE vue : basculer de vue la remontait, et la query repartait en
    // `network-only`. Une seule ici, partagée par les trois vues.
    useSupplyOrdersUpdatedOrCreatedSubscription({
        onData({ data: { data } }) {
            if (!data) {
                return;
            }
            supplyOrdersUpdatedOrCreatedHandler(data.supplyOrdersUpdatedOrCreated);
            toast.warning("Quelqu'un vient de modifier un ou plusieurs achat(s), la vue a été mise à jour !");
        },
    });

    const actions = useMemo(() => buildSupplyOrderActions(navigate), [navigate]);

    const actionGroups: PageActionGroup[] = useMemo(
        () => [
            {
                items: [
                    {
                        label: 'Légende des cartes',
                        icon: <Info />,
                        onClick: () => setIsCardInfoPopupOpen(true),
                    },
                ],
            },
        ],
        [],
    );

    if (supplyOrdersError) {
        throw new Error('Une erreur est survenue lors de la récupération des achats');
    }

    return (
        <Container>
            <Helmet title={`${PAGES.supplyOrders.label} · TOTADMIN`} />
            <PageHeader>
                <PageBreadcrumb>
                    <PageBreadcrumbSegment label={PAGES.supplyOrders.label} isCurrent />
                </PageBreadcrumb>
                <PageActions>
                    <PageActionsMenu groups={actionGroups} />
                </PageActions>
            </PageHeader>
            <Content>
                <DataView
                    data={supplyOrdersData?.supplyOrders ?? []}
                    getRowId={({ _id }) => _id}
                    isLoading={supplyOrdersLoading && !supplyOrdersData}
                    filters={SUPPLY_ORDER_FILTERS}
                    onFiltersChange={handleFiltersChange}
                    search={{
                        placeholder: 'Rechercher par fournisseur, numéro, entrepôt ou description…',
                        accessor: ({ _id, number, supplier, warehouseColumn, description }) => [
                            _id,
                            number?.toString(),
                            supplier.name,
                            warehouseColumn?.name,
                            description,
                        ],
                    }}
                    views={SUPPLY_ORDER_VIEWS}
                    actions={actions}
                />
            </Content>
            <TrelloCardInfoPopup isOpen={isCardInfoPopupOpen} setIsOpen={setIsCardInfoPopupOpen} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
